// BUILD-042 — My HR: the caller's own employment record and personal information.
//
// Unified employee workspace (2026-10-06/07, Owner-approved layout). Authority:
// docs/superpowers/decisions/2026-10-06-build-042-approved-unified-employee-layout.md. Rebuilt
// around a compact employee identity card above a grouped, four-category section nav (Employee /
// Work & Development / Records / Restricted — see workspace/sectionGroups.ts), one content
// section visible at a time, with internal sub-tabs where the approved reference shows them.
// This REPLACES the prior flat 9-item MyHrNav with the full approved 16-destination map; every
// existing working interaction (contact save, correction-request simulation, qualifications/
// memberships actions, viewer-role simulation) is preserved UNCHANGED — only reorganized under
// the new nav/content shell. Sections without a real backend yet render an honest Planned
// placeholder (workspace/PlannedSection.tsx) rather than being hidden or faked.
//
// The Restricted group is never shown here — see workspace/sectionGroups.ts's isRestrictedGroup
// for why (no real per-compartment permission signal exists in this frontend today).
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getMyHrRecord } from '../../api/client';
import { useMyAttention } from './attentionStore';
import { mySectionCounts, parseAttentionSection, sectionBadge, type BadgeView } from './workspace/attentionModel';
import { useAuth } from '../../context/AuthContext';
import { splitTermHistory, type TermLike } from './termHistory';
import { EmployeeCard } from './workspace/EmployeeCard';
import { WorkspaceNav } from './workspace/WorkspaceNav';
import { Breadcrumbs } from './workspace/Breadcrumbs';
import { MyHrOverview } from './workspace/MyHrOverview';
import { HrWorkspaceFrame } from './workspace/HrWorkspaceFrame';
import { PersonalContactsSelfSection } from './workspace/PersonalContactsSelfSection';
import { EmploymentPaySelfSection } from './workspace/EmploymentPaySelfSection';
import { TrainingQualificationsSelfSection } from './workspace/TrainingQualificationsSelfSection';
import { OnboardingSelfSection } from './workspace/OnboardingSelfSection';
import { DocumentsSelfSection } from './workspace/DocumentsSelfSection';
import { HistorySelfSection } from './workspace/HistorySelfSection';
import { PlannedSection } from './workspace/PlannedSection';
import { BenefitsSelfSection } from './workspace/BenefitsSelfSection';
import { PropertySection } from './workspace/PropertySection';
import { usePropertySchedule } from './workspace/usePropertySchedule';
import { SectionHeading, PlannedBadge } from './workspace/SectionHeading';
import { isBuilt, isNonRestricted, SECTION_NAV_GROUPS, subtitleFor, type SectionKey } from './workspace/sectionGroups';

interface Term extends TermLike {
  title: string | null;
  departmentId: string | null;
  managerEmployeeId: string | null;
  classification: string | null;
  reason?: string | null;
}
interface OwnRecord {
  employeeId: string;
  period: { periodNumber: number; hireDate: string; employmentStatus: string } | null;
  managerEmployeeId: string | null;
  managerName: string | null;
  termHistory: Term[];
}

export function MyHrTab() {
  const { user } = useAuth();
  const [record, setRecord] = useState<OwnRecord | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Oct 17: a link from the attention list lands directly on the right section (and opens the right document row).
  const [params] = useSearchParams();
  const linkedSection = parseAttentionSection(params.get('section'));
  const focusItem = params.get('item');
  const [activeSection, setActiveSection] = useState<SectionKey>(linkedSection ?? 'overview');
  useEffect(() => { if (linkedSection) setActiveSection(linkedSection); }, [linkedSection, focusItem]);
  const attention = useMyAttention();
  const counts = mySectionCounts(attention);
  const sectionBadges: Partial<Record<SectionKey, BadgeView>> = {
    documents: sectionBadge(counts.documents, counts.known, attention.status === 'error', 'Documents & Signatures'),
    checklists: sectionBadge(counts.checklists, counts.known, attention.status === 'error', 'Onboarding & Offboarding'),
  };
  // One schedule fetch feeds BOTH the Overview "Property issued" tile and the Equipment & Property list.
  const { load: propertyLoad, reload: reloadProperty } = usePropertySchedule('self', null);
  useEffect(() => { reloadProperty(); }, [activeSection, reloadProperty]);

  useEffect(() => {
    getMyHrRecord()
      .then(setRecord)
      .catch((e: Error & { status?: number }) => {
        if (e.status === 404) setNotFound(true);
        else setError(e.message || 'Could not load your HR record.');
      });
  }, []);

  if (error) return <div className="text-red-700">{error}</div>;
  if (notFound) return <div className="text-gray-500">No HR record exists for you yet.</div>;
  if (!record) return <div className="text-gray-500">Loading…</div>;

  const { current } = splitTermHistory(record.termHistory);
  const displayName = user?.name || 'Employee';
  // "Legal name" has no real field anywhere yet (see PersonalDetailsSection's own note) — the
  // only name this app actually has is the UniqueHQ login/display name, used here as the
  // honest stand-in so RequestCorrectionCard has something real to show as "current value"
  // rather than an invented placeholder.
  const legalName = displayName;
  const sectionLabel = SECTION_NAV_GROUPS.flatMap((g) => g.items).find((i) => i.key === activeSection)?.label ?? activeSection;

  return (
    // "Simulate each view for me" (2026-10-07): every governed-fact card (RequestCorrectionCard)
    // and the License section reads the simulated viewer role from MyHrViewerRoleContext, now
    // provided up in App.tsx and toggled from the top header bar (AppLayout.tsx's
    // HeaderViewerRoleToggle) instead of this page's own body — moved per Chris's request so
    // the toggle never affects this page's layout. See MyHrViewerRoleContext.tsx's header for
    // why this is visual simulation only, never a real permission change.
    <div>
      <Breadcrumbs items={[{ label: 'HR', to: '/hr' }, { label: 'My HR', to: '/hr' }, { label: sectionLabel }]} />
      <HrWorkspaceFrame railLabel="My HR menu" currentLabel={sectionLabel} testId="hr-my-frame" rail={<>
          <EmployeeCard
            info={{
              name: displayName,
              title: current?.title ?? null,
              departmentId: current?.departmentId ?? null,
              employmentStatus: record.period?.employmentStatus ?? null,
              hireDate: record.period?.hireDate ?? null,
              allowPhotoChange: true,
            }}
          />
          <WorkspaceNav
            active={activeSection}
            onSelect={setActiveSection}
            visible={isNonRestricted}
            context="self"
            badges={sectionBadges}
          />
        </>}>
        <div className="min-w-0">
          <SectionHeading
            title={sectionLabel}
            subtitle={subtitleFor(activeSection)}
            badge={!isBuilt(activeSection, 'self') ? <PlannedBadge /> : undefined}
          />
          {/* Oct 24: compact "Needs your action" first, then the employee's own employment; no giant zero-count cards, no sample claims, no admin controls. */}
          {activeSection === 'overview' && (
            <MyHrOverview
              info={{
                employmentStatus: record.period?.employmentStatus ?? null,
                hireDate: record.period?.hireDate ?? null,
                title: current?.title ?? null,
                classification: current?.classification ?? null,
                managerName: record.managerName ?? record.managerEmployeeId ?? null,
                latestChangeReason: current?.reason ?? null,
              }}
              onNavigate={setActiveSection}
              property={propertyLoad}
              attention={attention}
            />
          )}
          {activeSection === 'contacts' && (
            <PersonalContactsSelfSection
              employeeId={record.employeeId} employeeName={displayName}
              legalName={legalName} preferredName={null}
            />
          )}
          {activeSection === 'employment' && (
            <EmploymentPaySelfSection
              employeeId={record.employeeId} employeeName={displayName}
              termHistory={record.termHistory}
              employmentStatus={record.period?.employmentStatus} hireDate={record.period?.hireDate}
              managerName={record.managerName} managerEmployeeId={record.managerEmployeeId}
            />
          )}
          {activeSection === 'training' && (
            <TrainingQualificationsSelfSection employeeId={record.employeeId} employeeName={displayName} />
          )}
          {activeSection === 'checklists' && <OnboardingSelfSection key={`tasks-${focusItem ?? ''}`} focusTaskId={linkedSection === 'checklists' ? focusItem : null} />}
          {activeSection === 'documents' && <DocumentsSelfSection key={`docs-${focusItem ?? ''}`} focusAssignmentId={linkedSection === 'documents' ? focusItem : null} />}
          {activeSection === 'history' && <HistorySelfSection employeeId={record.employeeId} />}
          {activeSection === 'property' && <PropertySection load={propertyLoad} mode="self" />}
          {activeSection === 'benefits' && <BenefitsSelfSection />}
          {(activeSection === 'time' || activeSection === 'reviews' || activeSection === 'custom') && (
            <PlannedSection sectionKey={activeSection} employeeId={record.employeeId} />
          )}
        </div>
      </HrWorkspaceFrame>
    </div>
  );
}
